import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Download, ArrowRight, Sparkles } from 'lucide-react';
import useStats from '../hooks/useStats';
import AnimatedNumber from './AnimatedNumber';

const Hero = () => {
  const heroRef = useRef(null);
  const { stats, loading } = useStats();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToDownload = () => {
    const element = document.getElementById('download');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      ref={heroRef}
    >
      {/* Background with subtle overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/60 via-gray-800/50 to-gray-900/70 dark:from-gray-950/80 dark:via-gray-900/70 dark:to-gray-950/90 z-10"></div>
        <img
          src="https://images.unsplash.com/photo-1758611971431-c876c0a21346"
          alt="Freelancer success"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Animated background elements */}
      <div className="absolute inset-0 z-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 z-20 relative">
        <div className="max-w-5xl mx-auto text-center">
          {/* Content */}
          <div className="text-white space-y-8 opacity-0 transition-opacity duration-1000 hero-content">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/20">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              <span className="text-sm font-medium tracking-wide">Smart Freelance Marketplace</span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight tracking-tight">
              Connect. Hire.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400">
                Work. Grow.
              </span>
            </h1>

            <p className="text-xl md:text-2xl lg:text-3xl text-gray-200 leading-relaxed max-w-3xl mx-auto font-light">
              We provide a smart platform that connects clients with skilled freelancers quickly and efficiently.
            </p>

            <p className="text-base md:text-xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
              Whether you are a business looking for reliable services or a freelancer searching for work opportunities, Kaamhub makes the process simple, fast, and trusted.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                onClick={scrollToDownload}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <Download className="mr-2" />
                Download App
              </Button>
              <Button
                onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white/10 backdrop-blur-md text-white border-2 border-white/30 hover:bg-white/20 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-300"
              >
                Learn More
                <ArrowRight className="ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-12 max-w-2xl mx-auto">
              <div className="backdrop-blur-sm bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="text-4xl font-bold mb-1">
                  {loading ? '...' : stats ? <AnimatedNumber value={stats.users} /> : '2K+'}
                </div>
                <div className="text-gray-300 text-sm font-light">Users</div>
              </div>
              <div className="backdrop-blur-sm bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="text-4xl font-bold mb-1">
                  {loading ? '...' : stats ? <AnimatedNumber value={stats.posts} /> : '5K+'}
                </div>
                <div className="text-gray-300 text-sm font-light">Job Postings</div>
              </div>
              <div className="backdrop-blur-sm bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="text-4xl font-bold mb-1">
                  {loading ? '...' : stats ? <AnimatedNumber value={stats.applications} /> : '1K+'}
                </div>
                <div className="text-gray-300 text-sm font-light">Applications</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white rounded-full animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
